/**
 * TransactionsSection V2
 *
 * Zeigt Credit-Transaktionsverlauf mit Pagination.
 * - Credit Transactions Table
 * - Operation Type Badge
 * - Credit Amount with +/-
 *
 * Built with Tailwind CSS v4.1 and V2 components.
 * WCAG 2.1 Level AA + BFSG konform
 */

import { useQuery } from '@tanstack/react-query';
import { Receipt, Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getCreditTransactions } from '@/lib/api/credits';
import { formatCreditsWithSign, formatDateTime, formatNumber } from '@/lib/utils';
import {
  getTransactionTypeLabel,
  getTransactionTypeColor,
  type CreditTransaction,
} from '@/types/credits';
import {
  Card,
  Loading,
  EmptyState,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Button,
} from '@/components/v2/ui';

interface TransactionsSectionProps {
  canManageCredits?: boolean;
}

export function TransactionsSection({
  canManageCredits = false,
}: TransactionsSectionProps) {
  const { t } = useTranslation();
  // Pagination state
  const limit = 20;
  const offset = 0;

  // Fetch transactions
  const { data, isLoading } = useQuery({
    queryKey: ['credit-transactions', limit, offset],
    queryFn: () => getCreditTransactions(limit, offset),
  });

  const transactions = data?.transactions || [];

  const getOperationLabel = (opType: string | null): string => {
    if (!opType) return '-';
    const keyMap: Record<string, string> = {
      image_generation: 'billing.opImageGeneration',
      image_generation_4k: 'billing.opImageGeneration4k',
      image_generation_batch: 'billing.opImageGenerationBatch',
      upscale: 'billing.opUpscale',
      background_removal: 'billing.opBackgroundRemoval',
      image_analysis: 'billing.opImageAnalysis',
      text_generation_short: 'billing.opTextGenerationShort',
      text_generation_long: 'billing.opTextGenerationLong',
      image_convert: 'billing.opImageConvert',
      subscription_renewal: 'billing.opSubscriptionRenewal',
      credit_purchase: 'billing.opCreditPurchase',
      admin_adjustment: 'billing.opAdminAdjustment',
      initial_allocation: 'billing.opInitialAllocation',
    };
    const key = keyMap[opType];
    return key ? t(key) : opType;
  };

  return (
    <Card>
      <div className="p-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <h3 className="flex items-center gap-2 text-xl font-bold text-gray-900 dark:text-gray-50">
            <Receipt className="h-6 w-6" />
            {t('billing.creditHistory')}
          </h3>

          {/* Export Button */}
          {canManageCredits && transactions.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                // TODO: Implement CSV export for credits
                alert(t('billing.csvComingSoon'));
              }}
            >
              <Download className="h-4 w-4 mr-2" />
              {t('billing.exportCsv')}
            </Button>
          )}
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loading size="lg" />
            <p className="mt-4 text-gray-600 dark:text-gray-400">{t('common.loading')}</p>
          </div>
        ) : transactions.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('billing.columnDate')}</TableHead>
                  <TableHead>{t('billing.columnType')}</TableHead>
                  <TableHead>{t('billing.columnOperation')}</TableHead>
                  <TableHead>{t('billing.columnDescription')}</TableHead>
                  <TableHead className="text-right">{t('billing.columnCredits')}</TableHead>
                  <TableHead className="text-right">{t('billing.columnBalance')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.map((tx: CreditTransaction) => (
                  <TableRow key={tx.id}>
                    <TableCell>
                      <small className="text-gray-600 dark:text-gray-400">
                        {tx.created_at ? formatDateTime(tx.created_at) : '-'}
                      </small>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          tx.transaction_type === 'credit' || tx.transaction_type === 'refund'
                            ? 'success'
                            : tx.transaction_type === 'debit'
                              ? 'error'
                              : 'default'
                        }
                      >
                        {getTransactionTypeLabel(tx.transaction_type)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {getOperationLabel(tx.operation_type)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {tx.description || '-'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <strong className={getTransactionTypeColor(tx.transaction_type)}>
                        {formatCreditsWithSign(tx.credit_amount)}
                      </strong>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {formatNumber(tx.balance_after)}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <EmptyState
            icon={<Receipt className="h-12 w-12" />}
            title={t('billing.noTransactionsTitle')}
            description={t('billing.noTransactionsDescription')}
          />
        )}
      </div>
    </Card>
  );
}
